"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import { useLocation, useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { toast } from "sonner"
import { Plus, User, FileText, Search, Building2, Trash2, Edit, Eye, Phone } from "lucide-react"

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

const emptyCustomer = {
  name: "",
  email: "",
  phone: "",
  address: "",
}

export default function Customers() {
  const navigate = useNavigate()
  const location = useLocation()

  const [activeTab, setActiveTab] = useState("customers")
  const [customers, setCustomers] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [supplierSearch, setSupplierSearch] = useState("")
  const [openCustomer, setOpenCustomer] = useState(false)
  const [openSupplier, setOpenSupplier] = useState(false)
  const [editingCustomer, setEditingCustomer] = useState(null)
  const [viewCustomer, setViewCustomer] = useState(null)
  const [customerForm, setCustomerForm] = useState(emptyCustomer)
  const [supplierForm, setSupplierForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    opening_balance: 0,
  })

  useEffect(() => {
    fetchCustomers()
    fetchSuppliers()
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get("action") === "add") {
      setOpenCustomer(true)
      navigate("/customers", { replace: true })
    }
  }, [location.search, navigate])

  const tokenHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  })

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/customers`, tokenHeaders())
      setCustomers(res.data || [])
    } catch {
      toast.error("Failed to load customers")
    }
  }

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${API}/suppliers`, tokenHeaders())
      setSuppliers(res.data || [])
    } catch {
      toast.error("Failed to load suppliers")
    }
  }

  const resetCustomerForm = () => {
    setEditingCustomer(null)
    setCustomerForm(emptyCustomer)
  }

  const submitCustomer = async (e) => {
    e.preventDefault()
    try {
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, customerForm, tokenHeaders())
        toast.success("Customer updated successfully")
      } else {
        await axios.post(`${API}/customers`, customerForm, tokenHeaders())
        toast.success("Customer added successfully")
      }

      fetchCustomers()
      setOpenCustomer(false)
      resetCustomerForm()
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to save customer")
    }
  }

  const deleteCustomer = async (customer) => {
    if (!window.confirm(`Delete ${customer.name}?`)) return

    try {
      await axios.delete(`${API}/customers/${customer.id}`, tokenHeaders())
      toast.success("Customer deleted successfully")
      fetchCustomers()
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to delete customer")
    }
  }

  const addSupplier = async (e) => {
    e.preventDefault()
    try {
      await axios.post(`${API}/suppliers`, supplierForm, tokenHeaders())
      toast.success("Supplier added successfully")
      fetchSuppliers()
      setOpenSupplier(false)
      setSupplierForm({
        name: "",
        email: "",
        phone: "",
        address: "",
        opening_balance: 0,
      })
    } catch {
      toast.error("Failed to add supplier")
    }
  }

  const handleCreateInvoice = (customer) => {
    navigate("/invoices", {
      state: {
        customer: {
          id: customer.id,
          name: customer.name,
          phone: customer.phone,
          email: customer.email,
          address: customer.address,
        },
      },
    })
  }

  const filteredCustomers = customers.filter((c) =>
    `${c.name} ${c.email} ${c.phone}`.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const filteredSuppliers = suppliers.filter((s) =>
    `${s.name} ${s.phone}`.toLowerCase().includes(supplierSearch.toLowerCase())
  )

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-7xl space-y-4 p-4 md:p-6 lg:p-8">
        <div className="flex items-center gap-2">
          <Dialog
            open={openCustomer}
            onOpenChange={(next) => {
              setOpenCustomer(next)
              if (!next) resetCustomerForm()
            }}
          >
            <DialogTrigger asChild>
              <Button size="lg" className="hidden rounded-2xl sm:inline-flex">
                <Plus className="mr-2 h-4 w-4" />
                {editingCustomer ? "Edit Customer" : "Add Customer"}
              </Button>
            </DialogTrigger>

            <DialogContent className="max-w-[95vw] rounded-3xl sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>{editingCustomer ? "Edit Customer" : "Add New Customer"}</DialogTitle>
              </DialogHeader>

              <form onSubmit={submitCustomer} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    placeholder="Enter customer name"
                    value={customerForm.name}
                    onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })}
                    required
                    className="h-12 rounded-2xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="customer@example.com"
                    value={customerForm.email}
                    onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
                    className="h-12 rounded-2xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    placeholder="+91 98765 43210"
                    value={customerForm.phone}
                    onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
                    className="h-12 rounded-2xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    placeholder="Street address, city, state"
                    value={customerForm.address}
                    onChange={(e) => setCustomerForm({ ...customerForm, address: e.target.value })}
                    className="h-12 rounded-2xl"
                  />
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {editingCustomer && (
                    <Button type="button" variant="outline" className="h-12 rounded-2xl" onClick={resetCustomerForm}>
                      Cancel Edit
                    </Button>
                  )}
                  <Button type="submit" className="h-12 rounded-2xl">
                    {editingCustomer ? "Update Customer" : "Add Customer"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {activeTab === "suppliers" && (
            <Dialog open={openSupplier} onOpenChange={setOpenSupplier}>
              <DialogTrigger asChild>
                <Button size="lg" variant="secondary" className="hidden rounded-2xl sm:inline-flex">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Supplier
                </Button>
              </DialogTrigger>

              <DialogContent className="max-w-[95vw] rounded-3xl sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>Add New Supplier</DialogTitle>
                </DialogHeader>

                <form onSubmit={addSupplier} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="supplier-name">Supplier Name</Label>
                    <Input
                      id="supplier-name"
                      placeholder="Enter supplier name"
                      value={supplierForm.name}
                      onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
                      required
                      className="h-12 rounded-2xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="supplier-phone">Phone Number</Label>
                    <Input
                      id="supplier-phone"
                      placeholder="+91 98765 43210"
                      value={supplierForm.phone}
                      onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })}
                      className="h-12 rounded-2xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="supplier-email">Email</Label>
                    <Input
                      id="supplier-email"
                      type="email"
                      placeholder="supplier@example.com"
                      value={supplierForm.email}
                      onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
                      className="h-12 rounded-2xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="supplier-address">Address</Label>
                    <Input
                      id="supplier-address"
                      placeholder="Street address, city, state"
                      value={supplierForm.address}
                      onChange={(e) => setSupplierForm({ ...supplierForm, address: e.target.value })}
                      className="h-12 rounded-2xl"
                    />
                  </div>

                  <Button type="submit" className="h-12 w-full rounded-2xl">
                    Add Supplier
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}

          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={activeTab === "customers" ? "Search customer..." : "Search supplier..."}
              className="h-10 rounded-full pl-10 pr-3 text-sm"
              value={activeTab === "customers" ? searchTerm : supplierSearch}
              onChange={(e) =>
                activeTab === "customers"
                  ? setSearchTerm(e.target.value)
                  : setSupplierSearch(e.target.value)
              }
            />
          </div>
          <div className="flex w-[8.5rem] gap-1 rounded-full border bg-muted/30 p-[3px] sm:w-auto">
            <Button
              onClick={() => setActiveTab("customers")}
              variant={activeTab === "customers" ? "default" : "ghost"}
              className="h-7 flex-1 rounded-full px-1 text-[10px]"
            >
              <User className="h-3 w-3" />
            </Button>

            <Button
              onClick={() => setActiveTab("suppliers")}
              variant={activeTab === "suppliers" ? "default" : "ghost"}
              className="h-7 flex-1 rounded-full px-1 text-[10px]"
            >
              <Building2 className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {activeTab === "customers" && (
          <div className="space-y-3">
            <Card className="border-border/60 shadow-sm">
              <CardContent className="p-2">
                <div className="space-y-1.5 md:hidden">
                  {filteredCustomers.length === 0 && (
                    <div className="rounded-3xl border border-dashed bg-muted/10 px-6 py-12 text-center text-sm text-muted-foreground">
                      No customers found
                    </div>
                  )}

                  {filteredCustomers.map((customer) => (
                    <Card key={customer.id} className="rounded-xl border shadow-sm">
                      <CardContent className="space-y-2 p-2.5">
                        <div
                          className="cursor-pointer"
                          onClick={() => navigate(`/customer/${customer.id}`)}
                        >
                          <div className="flex items-start gap-2">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                              <User className="h-4 w-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-2">
                                <p className="truncate text-sm font-semibold">{customer.name}</p>
                                <p className="text-xs font-semibold text-green-600">
                                ₹{Number(customer.total_bill || 0).toLocaleString("en-IN")}
                                </p>
                              </div>
                              <div className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                                <Phone className="h-3 w-3" />
                                <span className="truncate">{customer.phone || "-"}</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-4 gap-1 border-t pt-2">
                          <Button size="sm" variant="outline" className="h-7 rounded-md px-1.5 text-[10px]" onClick={() => setViewCustomer(customer)}>
                            View
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 rounded-md px-1.5 text-[10px]" onClick={() => handleCreateInvoice(customer)}>
                            Invoice
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 rounded-md px-1.5 text-[10px]"
                            onClick={() => {
                              setEditingCustomer(customer)
                              setCustomerForm({
                                name: customer.name || "",
                                email: customer.email || "",
                                phone: customer.phone || "",
                                address: customer.address || "",
                              })
                              setOpenCustomer(true)
                            }}
                          >
                            Edit
                          </Button>
                          <Button size="sm" variant="destructive" className="h-7 rounded-md px-1.5 text-[10px]" onClick={() => deleteCustomer(customer)}>
                            Delete
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <div className="hidden overflow-x-auto md:block">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>#</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead className="text-right">Total Billing</TableHead>
                        <TableHead>Address</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredCustomers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan="7" className="py-8 text-center text-muted-foreground">
                            No customers found
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredCustomers.map((customer, index) => (
                          <TableRow
                            key={customer.id}
                            className="cursor-pointer hover:bg-muted/40"
                            onClick={() => navigate(`/customer/${customer.id}`)}
                          >
                            <TableCell>{index + 1}</TableCell>
                            <TableCell className="font-medium">{customer.name}</TableCell>
                            <TableCell>{customer.email || "-"}</TableCell>
                            <TableCell>{customer.phone || "-"}</TableCell>
                            <TableCell className="text-right font-semibold text-green-600">
                              ₹{Number(customer.total_bill || 0).toLocaleString("en-IN")}
                            </TableCell>
                            <TableCell className="max-w-xs truncate">{customer.address || "-"}</TableCell>
                            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                              <div className="flex justify-end gap-2">
                                <Button size="sm" variant="outline" onClick={() => handleCreateInvoice(customer)}>
                                  <FileText className="mr-2 h-4 w-4" />
                                  Invoice
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingCustomer(customer)
                                    setCustomerForm({
                                      name: customer.name || "",
                                      email: customer.email || "",
                                      phone: customer.phone || "",
                                      address: customer.address || "",
                                    })
                                    setOpenCustomer(true)
                                  }}
                                >
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => deleteCustomer(customer)}>
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <div className="px-1 text-xs text-muted-foreground">
              Showing {filteredCustomers.length} of {customers.length} customers
            </div>
          </div>
        )}

        <Dialog open={!!viewCustomer} onOpenChange={(open) => !open && setViewCustomer(null)}>
          <DialogContent className="max-w-[95vw] rounded-3xl sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Customer Details</DialogTitle>
            </DialogHeader>
            {viewCustomer && (
              <div className="space-y-3 text-sm">
                <div>
                  <p className="font-semibold text-base">{viewCustomer.name}</p>
                  <p className="text-green-600 font-semibold">
                    ₹{Number(viewCustomer.total_bill || 0).toLocaleString("en-IN")}
                  </p>
                </div>
                <div className="space-y-2 text-muted-foreground">
                  <p>Phone: {viewCustomer.phone || "-"}</p>
                  <p>Email: {viewCustomer.email || "-"}</p>
                  <p>Address: {viewCustomer.address || "-"}</p>
                  <p>Total Invoices: {viewCustomer.total_invoices || 0}</p>
                </div>
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <Button variant="outline" className="rounded-2xl" onClick={() => setViewCustomer(null)}>
                    Close
                  </Button>
                  <Button className="rounded-2xl" onClick={() => handleCreateInvoice(viewCustomer)}>
                    Create Invoice
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {activeTab === "suppliers" && (
          <div className="space-y-4">
            <Card className="border-border/60 shadow-sm">
              <CardHeader className="border-b bg-muted/20">
                <CardTitle className="text-base sm:text-lg">Suppliers</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid gap-4 md:hidden">
                  {filteredSuppliers.map((supplier) => (
                    <Card key={supplier.id} className="rounded-3xl border shadow-sm">
                      <CardContent className="space-y-3 p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-secondary/10 text-secondary">
                            <Building2 className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="font-semibold">{supplier.name}</p>
                            <p className="text-sm text-muted-foreground">{supplier.phone || "-"}</p>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground">{supplier.email || "-"}</p>
                        <p className="text-sm text-muted-foreground">{supplier.address || "-"}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <div className="hidden overflow-x-auto md:block">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>#</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Address</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredSuppliers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan="5" className="py-8 text-center text-muted-foreground">
                            No suppliers found
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredSuppliers.map((supplier, index) => (
                          <TableRow key={supplier.id}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell className="font-medium">{supplier.name}</TableCell>
                            <TableCell>{supplier.phone || "-"}</TableCell>
                            <TableCell>{supplier.email || "-"}</TableCell>
                            <TableCell className="max-w-xs truncate">{supplier.address || "-"}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}
